import { useState, useEffect, useRef } from "react";
import { useApi } from "../apiHelpers";
import { DataTable } from "../components/table/DataTable";
import PageLayout from "../components/ui/PageLayout";
import { buttonClass } from "../components/ui/Button";
import { Checkbox } from "../components/ui/Checkbox";
import { Field, FieldRow, TextArea } from "../components/ui/Field";
import { Select } from "../components/ui/Select";
import { Tag } from "../components/ui/Tag";
import { CATEGORY } from "../components/ui/categoryColors";
import { Alert } from "../components/ui/Alert";
import Reveal from "../components/ui/Reveal";
import { Dialog } from "../components/ui/Dialog";
import { EmptyState } from "../components/ui/EmptyState";
import { LogoSpinner } from "../components/ui/LogoSpinner";

const ITEM_TYPES = ['drone', 'battery', 'charger', 'ground_station', 'other'];

// ---------------------------------------------------------------------------
// The two tables' columns (the shared DataTable, one per tab).
//
// Module scope, per the DataTable contract — the arrays never change identity
// and no cell closes over page state. Edit and Delete arrive on the table's
// `meta` and are read at render time; never wrap one of these cells in
// React.memo, which would skip the parent's render and read a stale meta.
//
// The inactive row's dimming is per-row, so it is `rowProps` on the page rather
// than a class on any column: a static column class would dim every row.
//
// No `sortable` and no accessorFn: both lists are short reference data in the
// order the API returns them, which is what this page has always shown.
//
// This file is plain JavaScript: the columns are plain object literals, the
// table is rendered with no type argument, and nothing here is type-checked.
// The DataTable's generic exists for its .tsx consumers and costs this file
// nothing. The feature-flag booleans are edited in the modals below, never in
// a row: a row SHOWS the flags as chips, so nothing here needs an in-cell
// editor.
// ---------------------------------------------------------------------------

const CATALOG_COLUMNS = [
    {
        id: 'name',
        header: 'Name',
        meta: { cellClass: 'text-white' },
        cell: ({ row }) => (
            <>
                <div>{row.original.name}</div>
                {row.original.sku && <div className="text-xs text-on-surface-variant">SKU: {row.original.sku}</div>}
            </>
        )
    },
    {
        id: 'item_type',
        header: 'Type',
        meta: { cellClass: 'text-on-surface capitalize' },
        cell: ({ row }) => row.original.item_type
    },
    {
        id: 'weight_oz',
        header: 'Weight',
        meta: { cellClass: 'text-on-surface', numeric: true },
        cell: ({ row }) => (row.original.weight_oz ? `${row.original.weight_oz} oz` : '-')
    },
    {
        id: 'dimensions',
        header: 'Dimensions',
        meta: { cellClass: 'text-on-surface text-xs' },
        cell: ({ row }) => {
            const item = row.original;
            return item.length_in && item.width_in && item.height_in
                ? `${item.length_in}" x ${item.width_in}" x ${item.height_in}"`
                : '-';
        }
    },
    {
        id: 'requirements',
        header: 'Requirements',
        cell: ({ row }) => {
            const item = row.original;
            return (
                <div className="flex flex-wrap gap-1">
                    {item.requires_serial_number && (
                        <Tag hex={CATEGORY.blue.ink.hex} size="sm">S/N</Tag>
                    )}
                    {item.requires_hazmat && (
                        <Tag hex={CATEGORY.red.ink.hex} size="sm">Hazmat</Tag>
                    )}
                    {item.requires_own_box && (
                        <Tag hex={CATEGORY.purple.ink.hex} size="sm">Own Box</Tag>
                    )}
                    {item.is_fragile && (
                        <Tag hex={CATEGORY.yellow.ink.hex} size="sm">Fragile</Tag>
                    )}
                </div>
            );
        }
    },
    {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
            <Tag tone={row.original.active ? 'good' : 'neutral'}>
                {row.original.active ? 'Active' : 'Inactive'}
            </Tag>
        )
    },
    {
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
            const { onEdit, onDelete } = info.table.options.meta;
            const item = info.row.original;
            return (
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => onEdit(item)}
                        className={buttonClass('quiet', 'sm')}
                    >
                        Edit
                    </button>
                    <button
                        type="button"
                        onClick={() => onDelete(item.id)}
                        className={buttonClass('danger-quiet', 'sm')}
                    >
                        Delete
                    </button>
                </div>
            );
        }
    }
];

const BOX_COLUMNS = [
    {
        id: 'name',
        header: 'Name',
        meta: { cellClass: 'text-white' },
        cell: ({ row }) => (
            <>
                <div>{row.original.name}</div>
                {row.original.description && (
                    <div className="text-xs text-on-surface-variant">{row.original.description}</div>
                )}
            </>
        )
    },
    {
        id: 'dimensions',
        header: 'Dimensions',
        meta: { cellClass: 'text-on-surface' },
        cell: ({ row }) =>
            `${row.original.length_in}" x ${row.original.width_in}" x ${row.original.height_in}"`
    },
    {
        id: 'max_weight_oz',
        header: 'Max Weight',
        meta: { cellClass: 'text-on-surface', numeric: true },
        cell: ({ row }) => (row.original.max_weight_oz ? `${row.original.max_weight_oz} oz` : '-')
    },
    {
        id: 'tare_weight_oz',
        header: 'Tare Weight',
        meta: { cellClass: 'text-on-surface', numeric: true },
        cell: ({ row }) => (row.original.tare_weight_oz ? `${row.original.tare_weight_oz} oz` : '-')
    },
    {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
            <Tag tone={row.original.active ? 'good' : 'neutral'}>
                {row.original.active ? 'Active' : 'Inactive'}
            </Tag>
        )
    },
    {
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
            const { onEdit, onDelete } = info.table.options.meta;
            const box = info.row.original;
            return (
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => onEdit(box)}
                        className={buttonClass('quiet', 'sm')}
                    >
                        Edit
                    </button>
                    <button
                        type="button"
                        onClick={() => onDelete(box.id)}
                        className={buttonClass('danger-quiet', 'sm')}
                    >
                        Delete
                    </button>
                </div>
            );
        }
    }
];

const RMACatalogAdmin = () => {
    const api = useApi();

    // Data
    const [catalogItems, setCatalogItems] = useState([]);
    const [boxSizes, setBoxSizes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Tab state
    const [activeTab, setActiveTab] = useState("catalog");

    // Modal state
    const [showCatalogModal, setShowCatalogModal] = useState(false);
    const [showBoxModal, setShowBoxModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [editingBox, setEditingBox] = useState(null);

    // Form state for catalog item
    const [catalogForm, setCatalogForm] = useState({
        item_type: 'drone',
        name: '',
        sku: '',
        weight_oz: '',
        length_in: '',
        width_in: '',
        height_in: '',
        declared_value: '',
        requires_serial_number: true,
        requires_hazmat: false,
        requires_own_box: false,
        is_fragile: false,
        max_per_box: '',
        shipping_notes: '',
        active: true
    });

    // Form state for box size
    const [boxForm, setBoxForm] = useState({
        name: '',
        description: '',
        length_in: '',
        width_in: '',
        height_in: '',
        max_weight_oz: '',
        tare_weight_oz: '',
        carrier_box_code: '',
        active: true
    });

    // What each form opened with, to tell typed work from an untouched dialog.
    const openedCatalog = useRef(catalogForm);
    const openedBox = useRef(boxForm);
    // A record's integer columns prefill as numbers and come back from the fields as strings.
    const formText = (form) => JSON.stringify(form, (k, v) => (typeof v === 'number' ? String(v) : v));
    const catalogDirty = showCatalogModal && formText(catalogForm) !== formText(openedCatalog.current);
    const boxDirty = showBoxModal && formText(boxForm) !== formText(openedBox.current);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [itemsData, boxesData] = await Promise.all([
                api.get('/rma/catalog?active=false'),
                api.get('/rma/box-sizes?active=false')
            ]);
            setCatalogItems(itemsData);
            setBoxSizes(boxesData);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Catalog item handlers
    const openCatalogForm = (form) => {
        openedCatalog.current = form;
        setCatalogForm(form);
    };

    const openCatalogModal = (item = null) => {
        if (item) {
            setEditingItem(item);
            openCatalogForm({
                item_type: item.item_type || 'drone',
                name: item.name || '',
                sku: item.sku || '',
                weight_oz: item.weight_oz || '',
                length_in: item.length_in || '',
                width_in: item.width_in || '',
                height_in: item.height_in || '',
                declared_value: item.declared_value || '',
                requires_serial_number: item.requires_serial_number ?? true,
                requires_hazmat: item.requires_hazmat ?? false,
                requires_own_box: item.requires_own_box ?? false,
                is_fragile: item.is_fragile ?? false,
                max_per_box: item.max_per_box || '',
                shipping_notes: item.shipping_notes || '',
                active: item.active ?? true
            });
        } else {
            setEditingItem(null);
            openCatalogForm({
                item_type: 'drone',
                name: '',
                sku: '',
                weight_oz: '',
                length_in: '',
                width_in: '',
                height_in: '',
                declared_value: '',
                requires_serial_number: true,
                requires_hazmat: false,
                requires_own_box: false,
                is_fragile: false,
                max_per_box: '',
                shipping_notes: '',
                active: true
            });
        }
        setShowCatalogModal(true);
    };

    const saveCatalogItem = async () => {
        try {
            const payload = {
                ...catalogForm,
                weight_oz: catalogForm.weight_oz ? parseInt(catalogForm.weight_oz) : null,
                length_in: catalogForm.length_in ? parseFloat(catalogForm.length_in) : null,
                width_in: catalogForm.width_in ? parseFloat(catalogForm.width_in) : null,
                height_in: catalogForm.height_in ? parseFloat(catalogForm.height_in) : null,
                declared_value: catalogForm.declared_value ? parseFloat(catalogForm.declared_value) : null,
                max_per_box: catalogForm.max_per_box ? parseInt(catalogForm.max_per_box) : null
            };

            if (editingItem) {
                await api.patch(`/rma/catalog/${editingItem.id}`, { body: payload });
            } else {
                await api.post('/rma/catalog', { body: payload });
            }

            setShowCatalogModal(false);
            await fetchData();
        } catch (err) {
            setError(err.message);
        }
    };

    const deleteCatalogItem = async (id) => {
        if (!window.confirm('Delete this catalog item? This cannot be undone.')) return;
        try {
            await api.delete(`/rma/catalog/${id}`);
            await fetchData();
        } catch (err) {
            setError(err.message);
        }
    };

    // Box size handlers
    const openBoxForm = (form) => {
        openedBox.current = form;
        setBoxForm(form);
    };

    const openBoxModal = (box = null) => {
        if (box) {
            setEditingBox(box);
            openBoxForm({
                name: box.name || '',
                description: box.description || '',
                length_in: box.length_in || '',
                width_in: box.width_in || '',
                height_in: box.height_in || '',
                max_weight_oz: box.max_weight_oz || '',
                tare_weight_oz: box.tare_weight_oz || '',
                carrier_box_code: box.carrier_box_code || '',
                active: box.active ?? true
            });
        } else {
            setEditingBox(null);
            openBoxForm({
                name: '',
                description: '',
                length_in: '',
                width_in: '',
                height_in: '',
                max_weight_oz: '',
                tare_weight_oz: '',
                carrier_box_code: '',
                active: true
            });
        }
        setShowBoxModal(true);
    };

    const saveBoxSize = async () => {
        try {
            const payload = {
                ...boxForm,
                length_in: parseFloat(boxForm.length_in),
                width_in: parseFloat(boxForm.width_in),
                height_in: parseFloat(boxForm.height_in),
                max_weight_oz: boxForm.max_weight_oz ? parseInt(boxForm.max_weight_oz) : null,
                tare_weight_oz: boxForm.tare_weight_oz ? parseInt(boxForm.tare_weight_oz) : 0
            };

            if (editingBox) {
                await api.patch(`/rma/box-sizes/${editingBox.id}`, { body: payload });
            } else {
                await api.post('/rma/box-sizes', { body: payload });
            }

            setShowBoxModal(false);
            await fetchData();
        } catch (err) {
            setError(err.message);
        }
    };

    const deleteBoxSize = async (id) => {
        if (!window.confirm('Delete this box size? This cannot be undone.')) return;
        try {
            await api.delete(`/rma/box-sizes/${id}`);
            await fetchData();
        } catch (err) {
            setError(err.message);
        }
    };

    if (loading) {
        return <LogoSpinner label="Loading catalog data" />;
    }

    return (
        <div style={{ fontFamily: "RobotoMono, sans-serif" }}>
        <PageLayout kind="workspace" className="pt-4">
            <div>
                <h1 className="text-2xl font-bold text-white mb-6">RMA Catalog Admin</h1>

                <Reveal show={!!error}><Alert className="mb-4" onDismiss={() => setError(null)}>{error}</Alert></Reveal>

                {/* Tabs */}
                <div className="flex gap-2 mb-6">
                    <button
                        onClick={() => setActiveTab("catalog")}
                        className={`px-4 py-2 rounded font-medium transition-all ${
                            activeTab === "catalog"
                                ? "bg-primary text-black"
                                : "bg-gray-700 text-white hover:bg-gray-750"
                        }`}
                    >
                        Item Catalog ({catalogItems.length})
                    </button>
                    <button
                        onClick={() => setActiveTab("boxes")}
                        className={`px-4 py-2 rounded font-medium transition-all ${
                            activeTab === "boxes"
                                ? "bg-primary text-black"
                                : "bg-gray-700 text-white hover:bg-gray-750"
                        }`}
                    >
                        Box Sizes ({boxSizes.length})
                    </button>
                </div>

                {/* Catalog Items Tab.

                    The vessel is the shared table's: shell, radius, scroll
                    container and the face on every row, so the page draws no box
                    of its own around it. The section heading and its Add button
                    move into the `strip`, the band the component puts above the
                    header — same place on screen, one vessel instead of two. */}
                {activeTab === "catalog" && (
                    <DataTable
                        data={catalogItems}
                        columns={CATALOG_COLUMNS}
                        getRowId={(item) => String(item.id)}
                        meta={{ onEdit: openCatalogModal, onDelete: deleteCatalogItem }}
                        rowProps={(item) => ({ className: item.active ? '' : 'opacity-50' })}
                        strip={
                            <>
                                <h2 className="font-bold text-white">Item Catalog</h2>
                                <button
                                    onClick={() => openCatalogModal()}
                                    className={`${buttonClass('filled')} ml-auto`}
                                >
                                    + Add Item
                                </button>
                            </>
                        }
                        empty={<EmptyState variant="area">No catalog items. Add your first item.</EmptyState>}
                    />
                )}

                {/* Box Sizes Tab */}
                {activeTab === "boxes" && (
                    <DataTable
                        data={boxSizes}
                        columns={BOX_COLUMNS}
                        getRowId={(box) => String(box.id)}
                        meta={{ onEdit: openBoxModal, onDelete: deleteBoxSize }}
                        rowProps={(box) => ({ className: box.active ? '' : 'opacity-50' })}
                        strip={
                            <>
                                <h2 className="font-bold text-white">Box Sizes</h2>
                                <button
                                    onClick={() => openBoxModal()}
                                    className={`${buttonClass('filled')} ml-auto`}
                                >
                                    + Add Box Size
                                </button>
                            </>
                        }
                        empty={<EmptyState variant="area">No box sizes. Add your first box size.</EmptyState>}
                    />
                )}
            </div>

            <Dialog
                open={showCatalogModal}
                dismissible={false}
                dirty={catalogDirty}
                onClose={() => setShowCatalogModal(false)}
                title={editingItem ? 'Edit Catalog Item' : 'Add Catalog Item'}
                size="xl"
                footer={(
                    <>
                        <button
                            onClick={() => setShowCatalogModal(false)}
                            className={buttonClass('quiet')}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={saveCatalogItem}
                            className={buttonClass('filled')}
                        >
                            {editingItem ? 'Save Changes' : 'Add Item'}
                        </button>
                    </>
                )}
            >
                <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <FieldRow label="Item Type *">
                                    <Select
                                        label="Item type"
                                        value={catalogForm.item_type}
                                        onChange={(key) => setCatalogForm({...catalogForm, item_type: key})}
                                        options={ITEM_TYPES.map(type => ({ key: type, label: type.charAt(0).toUpperCase() + type.slice(1) }))}
                                    />
                                </FieldRow>
                                <FieldRow label="Name *">
                                    <Field
                                        value={catalogForm.name}
                                        onChange={(e) => setCatalogForm({...catalogForm, name: e.target.value})}
                                        placeholder="e.g., Gen2 Drone"
                                    />
                                </FieldRow>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <FieldRow label="SKU">
                                    <Field
                                        value={catalogForm.sku}
                                        onChange={(e) => setCatalogForm({...catalogForm, sku: e.target.value})}
                                        placeholder="Internal SKU"
                                    />
                                </FieldRow>
                                <FieldRow label="Declared Value ($)">
                                    <Field
                                        type="number"
                                        step="0.01"
                                        value={catalogForm.declared_value}
                                        onChange={(e) => setCatalogForm({...catalogForm, declared_value: e.target.value})}
                                        placeholder="For insurance"
                                    />
                                </FieldRow>
                            </div>

                            <div className="grid grid-cols-4 gap-4">
                                <FieldRow label="Weight (oz)">
                                    <Field
                                        type="number"
                                        value={catalogForm.weight_oz}
                                        onChange={(e) => setCatalogForm({...catalogForm, weight_oz: e.target.value})}
                                    />
                                </FieldRow>
                                <FieldRow label="Length (in)">
                                    <Field
                                        type="number"
                                        step="0.01"
                                        value={catalogForm.length_in}
                                        onChange={(e) => setCatalogForm({...catalogForm, length_in: e.target.value})}
                                    />
                                </FieldRow>
                                <FieldRow label="Width (in)">
                                    <Field
                                        type="number"
                                        step="0.01"
                                        value={catalogForm.width_in}
                                        onChange={(e) => setCatalogForm({...catalogForm, width_in: e.target.value})}
                                    />
                                </FieldRow>
                                <FieldRow label="Height (in)">
                                    <Field
                                        type="number"
                                        step="0.01"
                                        value={catalogForm.height_in}
                                        onChange={(e) => setCatalogForm({...catalogForm, height_in: e.target.value})}
                                    />
                                </FieldRow>
                            </div>

                            <FieldRow label="Max Per Box">
                                <Field
                                    type="number"
                                    value={catalogForm.max_per_box}
                                    onChange={(e) => setCatalogForm({...catalogForm, max_per_box: e.target.value})}
                                    placeholder="Leave empty for no limit"
                                />
                            </FieldRow>

                            <div className="grid grid-cols-2 gap-4">
                                <Checkbox
                                    checked={catalogForm.requires_serial_number}
                                    onChange={(e) => setCatalogForm({...catalogForm, requires_serial_number: e.target.checked})}
                                    label="Requires Serial Number"
                                />
                                <Checkbox
                                    checked={catalogForm.requires_hazmat}
                                    onChange={(e) => setCatalogForm({...catalogForm, requires_hazmat: e.target.checked})}
                                    label="Requires Hazmat Sticker"
                                />
                                <Checkbox
                                    checked={catalogForm.requires_own_box}
                                    onChange={(e) => setCatalogForm({...catalogForm, requires_own_box: e.target.checked})}
                                    label="Requires Own Box"
                                />
                                <Checkbox
                                    checked={catalogForm.is_fragile}
                                    onChange={(e) => setCatalogForm({...catalogForm, is_fragile: e.target.checked})}
                                    label="Fragile"
                                />
                            </div>

                            <FieldRow label="Shipping Notes">
                                <TextArea
                                    value={catalogForm.shipping_notes}
                                    onChange={(e) => setCatalogForm({...catalogForm, shipping_notes: e.target.value})}
                                    rows={3}
                                    placeholder="Special instructions for shipping this item..."
                                />
                            </FieldRow>

                            <Checkbox
                                checked={catalogForm.active}
                                onChange={(e) => setCatalogForm({...catalogForm, active: e.target.checked})}
                                label="Active (available for RMA submission)"
                            />
                        
                </div>
            </Dialog>

            <Dialog
                open={showBoxModal}
                dismissible={false}
                dirty={boxDirty}
                onClose={() => setShowBoxModal(false)}
                title={editingBox ? 'Edit Box Size' : 'Add Box Size'}
                size="lg"
                footer={(
                    <>
                        <button
                            onClick={() => setShowBoxModal(false)}
                            className={buttonClass('quiet')}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={saveBoxSize}
                            className={buttonClass('filled')}
                        >
                            {editingBox ? 'Save Changes' : 'Add Box Size'}
                        </button>
                    </>
                )}
            >
                <div className="space-y-4">
                            <FieldRow label="Name *">
                                <Field
                                    value={boxForm.name}
                                    onChange={(e) => setBoxForm({...boxForm, name: e.target.value})}
                                    placeholder="e.g., Small, Medium, Drone Box"
                                />
                            </FieldRow>

                            <FieldRow label="Description">
                                <Field
                                    value={boxForm.description}
                                    onChange={(e) => setBoxForm({...boxForm, description: e.target.value})}
                                    placeholder="What this box is used for"
                                />
                            </FieldRow>

                            <div className="grid grid-cols-3 gap-4">
                                <FieldRow label="Length (in) *">
                                    <Field
                                        type="number"
                                        step="0.01"
                                        value={boxForm.length_in}
                                        onChange={(e) => setBoxForm({...boxForm, length_in: e.target.value})}
                                    />
                                </FieldRow>
                                <FieldRow label="Width (in) *">
                                    <Field
                                        type="number"
                                        step="0.01"
                                        value={boxForm.width_in}
                                        onChange={(e) => setBoxForm({...boxForm, width_in: e.target.value})}
                                    />
                                </FieldRow>
                                <FieldRow label="Height (in) *">
                                    <Field
                                        type="number"
                                        step="0.01"
                                        value={boxForm.height_in}
                                        onChange={(e) => setBoxForm({...boxForm, height_in: e.target.value})}
                                    />
                                </FieldRow>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <FieldRow label="Max Weight (oz)">
                                    <Field
                                        type="number"
                                        value={boxForm.max_weight_oz}
                                        onChange={(e) => setBoxForm({...boxForm, max_weight_oz: e.target.value})}
                                        placeholder="Content weight limit"
                                    />
                                </FieldRow>
                                <FieldRow label="Tare Weight (oz)">
                                    <Field
                                        type="number"
                                        value={boxForm.tare_weight_oz}
                                        onChange={(e) => setBoxForm({...boxForm, tare_weight_oz: e.target.value})}
                                        placeholder="Box + packing"
                                    />
                                </FieldRow>
                            </div>

                            <FieldRow label="Carrier Box Code">
                                <Field
                                    value={boxForm.carrier_box_code}
                                    onChange={(e) => setBoxForm({...boxForm, carrier_box_code: e.target.value})}
                                    placeholder="FedEx/UPS specific code"
                                />
                            </FieldRow>

                            <Checkbox
                                checked={boxForm.active}
                                onChange={(e) => setBoxForm({...boxForm, active: e.target.checked})}
                                label="Active"
                            />
                        
                </div>
            </Dialog>
        </PageLayout>
        </div>
    );
};

export default RMACatalogAdmin;
